import { CheckCircleOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Inventory } from '@hefe/models';
import { currentUserSelector, InventoryApi } from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import { Button, List, Popconfirm } from 'antd';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { openInventory } from '../../services/inventory.service';
import UserInventoryListItemShareComponent from '../user-inventory-list-item-share/user-inventory-list-item-share.component';

interface UserInventoryListItemComponentProps {
  inventory: Inventory;
}

export default function UserInventoryListItemComponent(
  props: UserInventoryListItemComponentProps
) {
  const { inventory } = props;

  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);

  const [confirmTransfer, { isLoading: isConfirmingTransfer }] =
    InventoryApi.useConfirmTransferMutation();

  const description = useMemo(() => {
    if (!inventory.possessor_id) {
      return t('inventory.transfers.receive.description');
    }

    if (inventory.possessor_id === user?.id && inventory.possessor_since) {
      return t('inventory.possessor.you_info', {
        date: formatDateTime(inventory?.possessor_since),
      });
    }

    return '';
  }, [inventory, user]);

  const actions = useMemo(() => {
    const returnValue = [];

    if (!inventory.possessor_id) {
      returnValue.push(
        <Popconfirm
          key="receive"
          title={t('inventory.transfers.receive.confirm.description')}
          disabled={isConfirmingTransfer}
          onConfirm={() => confirmTransfer(inventory)}
        >
          <Button
            key="receive"
            icon={<CheckCircleOutlined />}
            loading={isConfirmingTransfer}
          />
        </Popconfirm>
      );
    }

    if (inventory.possessor_id) {
      returnValue.push(
        <UserInventoryListItemShareComponent
          key="share"
          inventory={inventory}
        />
      );
    }

    return returnValue;
  }, [confirmTransfer, inventory, isConfirmingTransfer]);

  const onInventoryClick = (inventory: Inventory) => {
    openInventory(inventory, dispatch);
  };

  return (
    <List.Item
      key={inventory.id}
      extra={<Button.Group>{actions}</Button.Group>}
    >
      <div
        onClick={() => onInventoryClick(inventory)}
        style={{ cursor: 'pointer', width: '100%' }}
      >
        <List.Item.Meta title={inventory.name} description={description} />
      </div>
    </List.Item>
  );
}
